import { Skeleton } from "@/components/ui/skeleton";

/* ─── Shared building blocks ─── */

const NavbarSkeleton = () => (
  <div className="h-14 border-b border-border bg-background/80 backdrop-blur-sm" />
);

const HeaderSkeleton = ({ subtitle = true }: { subtitle?: boolean }) => (
  <div className="space-y-1.5">
    {subtitle && <Skeleton className="h-3.5 w-32" />}
    <Skeleton className="h-7 w-48" />
  </div>
);

const CardSkeleton = ({ className = "h-20" }: { className?: string }) => (
  <Skeleton className={`rounded-xl ${className}`} />
);

const StatRowSkeleton = ({ count = 4 }: { count?: number }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
    {Array.from({ length: count }).map((_, i) => (
      <Skeleton key={i} className="h-16 rounded-xl" />
    ))}
  </div>
);

/* ─── Page skeletons ─── */

/** Inbox page skeleton — mimics header + stats + card list */
export const InboxSkeleton = () => (
  <div className="mx-auto max-w-3xl px-4 py-6 sm:py-10 space-y-5">
    <HeaderSkeleton />
    <StatRowSkeleton count={3} />
    <Skeleton className="h-10 w-full rounded-lg" /> {/* action bar */}
    <div className="space-y-3">
      {[1, 2, 3].map(i => <CardSkeleton key={i} className="h-24" />)}
    </div>
  </div>
);

/** Account page skeleton — mimics header + tab bar + form fields */
export const AccountSkeleton = () => (
  <div className="mx-auto max-w-2xl px-4 py-6 sm:py-10 space-y-5">
    <HeaderSkeleton subtitle={false} />
    <Skeleton className="h-10 w-64 rounded-lg" /> {/* tab bar */}
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <Skeleton className="h-16 w-16 rounded-full" /> {/* avatar */}
        <div className="space-y-2 flex-1">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3.5 w-48" />
        </div>
      </div>
      {[1, 2, 3].map(i => (
        <div key={i} className="space-y-1.5">
          <Skeleton className="h-3.5 w-20" />
          <Skeleton className="h-10 w-full rounded-lg" />
        </div>
      ))}
    </div>
  </div>
);

/** Analytics page skeleton — mimics header + stats + charts */
export const AnalyticsSkeleton = () => (
  <div className="mx-auto max-w-5xl px-4 py-6 sm:py-10 space-y-5">
    <HeaderSkeleton />
    <StatRowSkeleton />
    <Skeleton className="h-56 w-full rounded-xl" /> {/* trend chart */}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Skeleton className="h-44 rounded-xl" />
      <Skeleton className="h-44 rounded-xl" />
    </div>
  </div>
);

/** Agency inbox skeleton — mimics header + pills + card list */
export const AgencyInboxSkeleton = () => (
  <div className="mx-auto max-w-4xl px-4 py-6 sm:py-10 space-y-5 animate-fade-in">
    <HeaderSkeleton />
    <StatRowSkeleton count={4} />
    <div className="flex gap-2">
      {[1, 2, 3].map(i => <Skeleton key={i} className="h-8 w-20 rounded-full" />)}
    </div>
    <Skeleton className="h-10 w-full rounded-lg" />
    <div className="space-y-3">
      {[1, 2, 3].map(i => <CardSkeleton key={i} className="h-24" />)}
    </div>
  </div>
);

/** Agency roster skeleton — mimics header + search + card grid */
export const AgencyRosterSkeleton = () => (
  <div className="mx-auto max-w-4xl px-4 py-6 sm:py-10 space-y-5 animate-fade-in">
    <div className="flex items-center justify-between">
      <HeaderSkeleton />
      <Skeleton className="h-9 w-28 rounded-lg" />
    </div>
    <Skeleton className="h-10 w-full rounded-lg" /> {/* search bar */}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {[1, 2, 3, 4].map(i => (
        <Skeleton key={i} className="h-28 rounded-xl" />
      ))}
    </div>
  </div>
);

/** Agency settings skeleton — mimics header + tab bar + form */
export const AgencySettingsSkeleton = () => (
  <div className="mx-auto max-w-3xl px-4 py-6 sm:py-10 space-y-5 animate-fade-in">
    <HeaderSkeleton />
    <Skeleton className="h-10 w-72 rounded-lg" /> {/* tab bar */}
    <div className="space-y-4">
      {[1, 2, 3].map(i => (
        <div key={i} className="space-y-1.5">
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="h-10 w-full rounded-lg" />
        </div>
      ))}
    </div>
  </div>
);

/** Admin tab section skeleton — table-style rows */
export const AdminSectionSkeleton = () => (
  <div className="space-y-3">
    <div className="flex items-center gap-3">
      <Skeleton className="h-9 w-9 rounded-lg" />
      <Skeleton className="h-5 w-40" />
    </div>
    {[1, 2, 3, 4, 5].map(i => (
      <div key={i} className="flex items-center gap-3 py-2">
        <Skeleton className="h-4 w-4 rounded" />
        <Skeleton className="h-4 flex-1" />
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-16" />
      </div>
    ))}
  </div>
);

/** Minimal full-page skeleton for auth guards and route fallbacks — uses branded logo loader */
export const AuthGuardSkeleton = () => (
  <div className="min-h-screen bg-background">
    <NavbarSkeleton />
    <div className="flex items-center justify-center py-32 sm:py-44">
      <div className="flex flex-col items-center gap-3 animate-fade-in">
        <img
          src="/logo.png"
          alt="SendCollab"
          className="opacity-0 animate-logo-breathe select-none pointer-events-none h-7"
          width={112}
          height={28}
          draggable={false}
        />
        <div className="flex gap-1">
          <span className="h-1 w-1 rounded-full bg-primary/40 animate-logo-dot" style={{ animationDelay: "0ms" }} />
          <span className="h-1 w-1 rounded-full bg-primary/40 animate-logo-dot" style={{ animationDelay: "150ms" }} />
          <span className="h-1 w-1 rounded-full bg-primary/40 animate-logo-dot" style={{ animationDelay: "300ms" }} />
        </div>
      </div>
    </div>
  </div>
);
